import type { CalcStep } from "@/lib/calc/steps";
import type { Scenario, ScenarioMetric } from "@/lib/calc/scenarios";
import type { TableColumn } from "@/lib/calc/table";
import { amortize, formatMonthsSaved, type LoanResult, type LoanRow } from "./loan";
import { paymentDate, formatMonthYear, formatFullDate, ymdToISO } from "./paymentDates";
import type { YMD } from "./dates";

/** Pure builders for the Loan Calculator's working, schedule columns and scenarios (the UI is LoanExtras.tsx). */
export type Money = (n: number) => string;

export interface LoanInputs { principal: number; annualPct: number; months: number; extra: number; method: "reducing" | "flat" }

const pct = (n: number) => `${+n.toFixed(4)}%`;

/** The working behind the result on screen. Every number is taken from `res`, the same object the result shows. */
export function loanSteps(inp: LoanInputs, res: LoanResult, first: YMD, m: Money): CalcStep[] {
  const r = inp.annualPct / 100 / 12;
  if (inp.method === "flat") {
    return [
      { label: "Total interest", formula: "Loan amount x annual rate x years (interest is charged on the original amount for the whole term)", inputs: { "Loan amount": m(inp.principal), "Annual rate": pct(inp.annualPct), Years: +(inp.months / 12).toFixed(4) }, result: m(res.totalInterest) },
      { label: "Monthly payment", formula: "(Loan amount + total interest) / number of payments", inputs: { "Loan amount": m(inp.principal), "Total interest": m(res.totalInterest), Payments: res.months }, result: m(res.payment) },
      { label: "Total paid", formula: "Loan amount + total interest", inputs: { "Loan amount": m(inp.principal), "Total interest": m(res.totalInterest) }, result: m(res.totalPaid) },
    ];
  }
  const monthlyRate = +r.toFixed(8);
  const row1 = res.rows[0];
  const steps: CalcStep[] = [
    { label: "Monthly interest rate", formula: "Annual rate / 100 / 12", inputs: { "Annual rate": pct(inp.annualPct) }, result: String(monthlyRate) },
    {
      label: "Monthly payment",
      formula: r === 0 ? "Loan amount / number of payments (the rate is 0)" : "Loan amount x rate / (1 - (1 + rate) ^ -payments)",
      inputs: { "Loan amount": m(inp.principal), "Monthly rate": String(monthlyRate), Payments: inp.months }, result: m(res.payment),
    },
  ];
  if (row1) {
    steps.push({ label: "Split of the first payment", formula: "Interest = balance x monthly rate; principal = payment - interest", inputs: { Balance: m(inp.principal), "Monthly rate": String(monthlyRate), Payment: m(res.payment) }, result: `interest ${m(row1.interest)}, principal ${m(row1.principal)}` });
  }
  steps.push({ label: "Number of payments", formula: inp.extra > 0 ? "Payments are made until the balance reaches 0; each month's extra amount goes straight to principal" : "The term in months", inputs: inp.extra > 0 ? { "Extra each month": m(inp.extra), "Term (months)": inp.months } : { "Term (months)": inp.months }, result: res.rows.length });
  steps.push({ label: "Total interest", formula: "Add up the interest part of every payment in the schedule", inputs: { Payments: res.rows.length }, result: m(res.totalInterest) });
  steps.push({ label: "Total paid", formula: "Loan amount + total interest", inputs: { "Loan amount": m(inp.principal), "Total interest": m(res.totalInterest) }, result: m(res.totalPaid) });
  if (inp.extra > 0 && res.interestSaved > 0) {
    steps.push({ label: "Interest and time saved by the extra payment", formula: "Interest without the extra payment - interest with it; and the payments saved", inputs: { "Interest without extra": m(res.baseInterest), "Interest with extra": m(res.totalInterest) }, result: `${m(res.interestSaved)} saved, ${formatMonthsSaved(res.monthsSaved)} sooner` });
  }
  if (res.rows.length > 0) {
    steps.push({ label: "Payoff date", formula: "The first payment date plus (number of payments - 1) months", inputs: { "First payment": formatFullDate(first), Payments: res.rows.length }, result: formatMonthYear(paymentDate(first, res.rows.length - 1)) });
  }
  return steps;
}

/** Schedule columns. Raw values go to the CSV; `format` is what the table shows. */
export function loanColumns(first: YMD, m: Money): TableColumn<LoanRow>[] {
  const r2 = (n: number) => +n.toFixed(2);
  return [
    { key: "month", header: "Month", get: (r) => r.month, align: "left" },
    { key: "date", header: "Payment date", get: (r) => ymdToISO(paymentDate(first, r.month - 1)), format: (r) => formatFullDate(paymentDate(first, r.month - 1)), align: "left" },
    { key: "payment", header: "Payment", get: (r) => r2(r.payment), format: (r) => m(r.payment), align: "right" },
    { key: "principal", header: "Principal", get: (r) => r2(r.principal), format: (r) => m(r.principal), align: "right" },
    { key: "interest", header: "Interest", get: (r) => r2(r.interest), format: (r) => m(r.interest), align: "right" },
    { key: "extra", header: "Extra", get: (r) => r2(r.extra), format: (r) => m(r.extra), align: "right" },
    { key: "balance", header: "Remaining balance", get: (r) => r2(r.balance), format: (r) => m(r.balance), align: "right" },
  ];
}

export const SCENARIO_LIMIT_EXTRA = 1_000_000_000;

/** Up to three scenarios of the same loan that differ only in the extra monthly payment. The first is always "no extra". */
export function loanScenarios(inp: Omit<LoanInputs, "method" | "extra">, extras: number[], first: YMD, m: Money = String): Scenario[] {
  const amounts = [0, ...extras.slice(0, 2).map((e) => (Number.isFinite(e) ? Math.min(Math.max(0, e), SCENARIO_LIMIT_EXTRA) : 0))];
  const base = amortize({ principal: inp.principal, annualPct: inp.annualPct, months: inp.months });
  return amounts.map((ex, i) => {
    const res = i === 0 ? base : amortize({ principal: inp.principal, annualPct: inp.annualPct, months: inp.months, extra: ex });
    const payoff = res.rows.length > 0 ? formatMonthYear(paymentDate(first, res.rows.length - 1)) : "";
    return {
      id: `s${i + 1}`,
      name: i === 0 ? "No extra payment" : `${m(ex)} extra/month`,
      values: {
        extra: ex, payment: res.payment, payments: res.rows.length, payoff, interest: res.totalInterest, total: res.totalPaid,
        interestSaved: Math.max(0, base.totalInterest - res.totalInterest),
      },
    };
  });
}

export function loanScenarioMetrics(m: Money): ScenarioMetric[] {
  const money = (v: number | string) => m(Number(v));
  const months = (v: number | string) => `${Math.round(Number(v))} ${Math.round(Number(v)) === 1 ? "month" : "months"}`;
  return [
    { key: "extra", label: "Extra each month", kind: "number", format: money },
    { key: "payment", label: "Regular monthly payment", kind: "number", format: money },
    { key: "payments", label: "Payments until paid off", kind: "number", format: months, better: "lower" },
    { key: "payoff", label: "Payoff month", kind: "text" },
    { key: "interest", label: "Total interest", kind: "number", format: money, better: "lower" },
    { key: "total", label: "Total paid", kind: "number", format: money, better: "lower" },
    { key: "interestSaved", label: "Interest saved vs no extra", kind: "number", format: money, better: "higher" },
  ];
}

/** One line for the visit's history. */
export function loanHistorySummary(inp: LoanInputs, res: LoanResult, m: Money): string {
  return `${m(inp.principal)} at ${pct(inp.annualPct)} for ${inp.months} months${inp.extra > 0 ? ` with ${m(inp.extra)} extra` : ""}: ${m(res.payment)}/month, ${m(res.totalInterest)} interest`;
}
